/**
 * Modal plugin — the replacement for vue-js-modal.
 *
 * vue-js-modal is Vue 2 only: it calls `new Vue()` internally, which throws
 * under Vue 3 before the application can even mount. It is replaced here
 * rather than swapped for another library so that the call sites and the
 * rendering both stay exactly as they were:
 *
 *   this.$modal.show('name')                 // a <Popup name="name"> in a template
 *   this.$modal.hide('name')
 *   this.$modal.show(Component, props, { name, width })   // mounted on the fly
 *
 * The markup keeps vue-js-modal's class names — `vm--container`,
 * `vm--overlay`, `vm--modal` — because `_popup.scss` styles those, so the
 * existing stylesheet applies untouched and the modals look identical.
 */

import { reactive } from 'vue';

// Shared state. `named` holds the modals declared in templates; `dynamic`
// holds the ones opened with a component, in the order they were opened.
const state = reactive({
	named: {},
	dynamic: []
});

const api = {
	/**
	 * Open a modal.
	 * @param {string|object} target - the name of a declared modal, or a component
	 * @param {object} props - props for a component modal
	 * @param {object} options - { name, width } for a component modal
	 */
	show(target, props = {}, options = {}) {
		if (typeof target === 'string') {
			state.named[target] = true;
			return;
		}

		const name = options.name || `modal-${state.dynamic.length + 1}`;
		// Reopening the same dynamic modal replaces it rather than stacking.
		const existing = state.dynamic.findIndex((entry) => entry.name === name);
		const entry = { name, component: target, props, options };

		if (existing >= 0) state.dynamic.splice(existing, 1, entry);
		else state.dynamic.push(entry);
	},

	/** Close a modal by name — works for both kinds. */
	hide(name) {
		if (Object.prototype.hasOwnProperty.call(state.named, name)) {
			state.named[name] = false;
			return;
		}

		const index = state.dynamic.findIndex((entry) => entry.name === name);
		if (index >= 0) state.dynamic.splice(index, 1);
	},

	/** Whether a declared modal is currently open. */
	isOpen(name) {
		return !!state.named[name];
	},

	/** Declared modals register themselves so `show` can find them. */
	register(name) {
		if (!(name in state.named)) state.named[name] = false;
	},

	state
};

export default {
	install(app) {
		app.config.globalProperties.$modal = api;
		// Also provided, so a composition-API component could inject it.
		app.provide('modal', api);
	}
};

export { api as modal, state as modalState };
